import { ReactComponent as EmptySchedule } from "assets/svg/empty-schedule.svg";
import SidesheetHeader from "components/Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-sidebar/components/sidesheet-header/SidesheetHeader";
import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import messages from "messages";
import moment from "moment-timezone";
import { useCallback, useMemo } from "react";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  closeSidesheet,
  selectFocusedDay,
  showAddToCalendar,
} from "store/calendarSidebarSlice";
import { PRO_CALENDAR_ANALYTICS_PAGE_NAME, track } from "utils/amplitude";
import getClasses from "utils/getClasses";
import TimeslotTitle from "../../../calendar-view/components/scrollable-calendar-view/components/timeslot-title/TimeslotTitle";
import styles from "./DayPreview.module.scss";
import DayPreviewBadge from "./day-preview-badge/DayPreviewBadge";
import useEventsForDay from "./hooks/use-events-for-day";

const AMPLITUDE_COMPONENT_NAME = "day-preview";

const DayPreview = () => {
  const dispatch = useDispatch();
  const { currentCalendarTimezone } = useCalendarTime();
  const focusedDay = useSelector(selectFocusedDay);

  const title = useMemo(
    () => moment.tz(focusedDay, currentCalendarTimezone).format("MMMM D, YYYY"),
    [focusedDay, currentCalendarTimezone],
  );

  const { eventsForDay } = useEventsForDay(focusedDay);

  const isEmpty = useMemo(() => eventsForDay.length === 0, [eventsForDay]);

  const addToCalendar = useCallback(
    () =>
      dispatch(
        showAddToCalendar({
          hasInitialTime: false,
          defaultStartTime: focusedDay,
        }),
      ),
    [dispatch, focusedDay],
  );

  const trackClick = (elementName: string) =>
    track(
      PRO_CALENDAR_ANALYTICS_PAGE_NAME,
      [AMPLITUDE_COMPONENT_NAME, elementName],
      "click",
    );

  return (
    <div className={getClasses(styles.container)}>
      <SidesheetHeader
        title={<TimeslotTitle>{title}</TimeslotTitle>}
        className={getClasses(styles["title"])}
        onClose={() => {
          trackClick("close-button");
          dispatch(closeSidesheet());
        }}
      />

      <div
        className={getClasses(styles["body"], isEmpty && styles["body--empty"])}
      >
        {isEmpty ? (
          <div className={styles["empty-content"]}>
            <EmptySchedule />
            <div className={styles["empty-content__copy"]}>
              {messages.nothingBookedForTheDay}
            </div>
            <Button
              variant={"primary"}
              onClick={() => {
                trackClick("add-to-calendar-button");
                addToCalendar();
              }}
            >
              {messages.addToCalendar}
            </Button>
          </div>
        ) : (
          <div className={styles["badges"]}>
            {eventsForDay.map((event) => (
              <DayPreviewBadge key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>
      {!isEmpty && (
        <div className={styles["footer"]}>
          <Button
            variant={"primary"}
            onClick={() => {
              trackClick("add-to-calendar-button");
              addToCalendar();
            }}
          >
            {messages.addToCalendar}
          </Button>
        </div>
      )}
    </div>
  );
};

export default DayPreview;
